import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { Chart, registerables } from "chart.js";
import { STYLES, PAYMENTS_DATASETS_LABELS } from "../../constants/analytics.constant";
import { DateState, PaymentAnalyticsDay, PaymentAnalyticsDto, PaymentBarState, PaymentsPercents, StatUnitDto } from "../../models/analytics.model";
import { AnalyticsService } from "../../services/analytics-data.service";
import { TranslateService } from "@ngx-translate/core";
import { UnsubscribeService } from "src/app/services/unsubscribe.service";
import { takeUntil } from "rxjs";
import { AnalyticsTranslationService } from "../../services/analytics-translation.service";

Chart.register(...registerables);

@Component({
  selector: "app-payments-analytics",
  templateUrl: "./payments-analytics.component.html",
  styleUrls: ["./payments-analytics.component.scss"],
  providers: [UnsubscribeService],
})
export class PaymentsAnalyticsComponent implements OnInit {
  chart: any;
  data: any;
  commonData: PaymentAnalyticsDto | null = null;
  dateState = DateState;
  dateFilter = DateState.Month;
  barState = PaymentBarState;
  barStates = new Set<PaymentBarState>();
  totalCommision: number = 0;
  totalPayments: number = 0;

  paymentStatistics: PaymentAnalyticsDay[] | null = null;

  percents: PaymentsPercents | null = null;

  isDataValid = true;
  isFirstCall = true;

  constructor(
    private analyticsService: AnalyticsService,
    private translateService: TranslateService,
    private unsubscribe$: UnsubscribeService,
    private analyticsTranslationService: AnalyticsTranslationService,
    private cdf: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadInfo(this.isFirstCall);

    this.translateService.onLangChange.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.updateLabelTranslations();
      this.setLabels();
      this.chart?.update();
    });
  }

  loadInfo(isFirstCall?: boolean) {
    this.analyticsService
      .getPaymentsAnalytics({
        period_type: this.dateFilter,
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res: PaymentAnalyticsDto) => {
          this.commonData = res;
          this.isFirstCall = false;
          this.totalCommision = res.commission_sum_total;
          this.totalPayments = res.payments_sum_total;

          this.isDataValid = res.payments_sum_total <= 0 ? false : true;

          this.percents = {
            linkSum: Math.round(
              ((res.total_statistics.find((s: any) => s.payment_type === "PAYMENT_LINK")?.sum || 0) / res.payments_sum_total) * 100
            ),
            qrSum: Math.round(
              ((res.total_statistics.find((s: any) => s.payment_type === "QR_CODE_PAYMENT")?.sum || 0) / res.payments_sum_total) * 100
            ),
            recurringSum: Math.round(
              ((res.total_statistics.find((s: any) => s.payment_type === "RECURRENT_PAYMENTS")?.sum || 0) / res.payments_sum_total) * 100
            ),
            websiteSum: Math.round(
              ((res.total_statistics.find((s: any) => s.payment_type === "PAYMENT_FROM_SITE")?.sum || 0) / res.payments_sum_total) * 100
            ),
          };

          this.paymentStatistics = res.payment_statistics.map((stat) => {
            const statistic = this.dateFilter === DateState.Year ? stat.month_statistics : stat.statistics;
            return {
              date: stat.date,

              totalCommission: statistic.reduce((sum: number, s: any) => sum + s.commission, 0),

              sitePaymentCommission: statistic.find((s: any) => s.payment_type === "PAYMENT_FROM_SITE")?.commission || 0,
              linkPaymentCommission: statistic.find((s: any) => s.payment_type === "PAYMENT_LINK")?.commission || 0,
              qrPaymentCommission: statistic.find((s: any) => s.payment_type === "QR_CODE_PAYMENT")?.commission || 0,
              recurringPaymentCommission: statistic.find((s: any) => s.payment_type === "RECURRENT_PAYMENTS")?.commission || 0,

              paymentByLinkSum: statistic.find((s: any) => s.payment_type === "PAYMENT_LINK")?.sum || 0,
              paymentByQrSum: statistic.find((s: any) => s.payment_type === "QR_CODE_PAYMENT")?.sum || 0,
              paymentByWebsiteSum: statistic.find((s: any) => s.payment_type === "PAYMENT_FROM_SITE")?.sum || 0,
              recurringPaymentSum: statistic.find((s: any) => s.payment_type === "RECURRENT_PAYMENTS")?.sum || 0,
            };
          });

          if (isFirstCall) {
            this.renderChar();
          } else {
            this.data.datasets = this.initializeChartData().datasets;
          }

          this.initAllBars();
          this.setLabels();
          this.chart.update();
        },
      });
  }

  setTimeFrame(dateState: DateState) {
    if (dateState === this.dateFilter) return;

    this.dateFilter = dateState;
    switch (dateState) {
      case DateState.Week: {
        this.dateFilter = dateState;
        break;
      }
      case DateState.Month: {
        this.dateFilter = dateState;
        break;
      }
      case DateState.Year:
        this.dateFilter = dateState;
        break;
    }

    this.loadInfo();
  }

  setData(state: PaymentBarState) {
    state === PaymentBarState.AllPayments ? this.toggleAllBars() : this.toggleBar(state);

    if (state === PaymentBarState.Commision && !this.barStates.has(state)) {
      this.totalCommision = 0;
    } else {
      this.configurateCommissions();
    }

    if (this.barStates.has(this.barState.Commision) && this.barStates.size === 1) {
      this.totalCommision = this.commonData?.commission_sum_total || 0;
    }

    this.chart.update();
  }

  private configurateCommissions() {
    const commissionBar = this.data.datasets.find((el: any) => el.label === "Комиссия");

    if (this.barStates.has(this.barState.Commision) && this.barStates.size === 1) {
      commissionBar.data = this.paymentStatistics?.map((stat) => stat.totalCommission);
    } else {
      commissionBar.data = this.setCommissions(this.paymentStatistics!);
    }

    this.setCommissions(this.paymentStatistics!);
  }

  private setCommissions(statistic: PaymentAnalyticsDay[]) {
    const totalCommissions = statistic.map((stat) => {
      let total = 0;
      if (this.barStates.has(this.barState.LinkPayment)) {
        total += stat.linkPaymentCommission;
      }
      if (this.barStates.has(this.barState.QrPayment)) {
        total += stat.qrPaymentCommission;
      }
      if (this.barStates.has(this.barState.RecurringPayment)) {
        total += stat.recurringPaymentCommission;
      }
      if (this.barStates.has(this.barState.SitePayment)) {
        total += stat.sitePaymentCommission;
      }

      return total;
    });

    this.totalCommision = totalCommissions.reduce((val, acc) => (acc += val), 0);
    return totalCommissions;
  }

  private setLabels() {
    if (!this.paymentStatistics) return;
    switch (this.dateFilter) {
      case DateState.Month:
        this.data.labels = this.analyticsTranslationService.mapDatesToLabels(
          this.paymentStatistics.map((el) => el.date),
          this.dateFilter
        );
        break;
      case DateState.Week:
        this.data.labels = this.analyticsTranslationService.mapDatesToLabels(
          this.paymentStatistics.map((el) => el.date),
          this.dateFilter
        );
        break;
      case DateState.Year:
        this.data.labels = this.analyticsTranslationService.mapDatesToLabels(
          this.paymentStatistics.map((el) => el.date),
          this.dateFilter
        );
        break;
    }
  }

  private updateLabelTranslations() {
    if (!this.data) return;

    this.data.datasets.forEach((dataset: any, index: number) => {
      dataset.label = this.translateService.instant(`ANALYTICS_PAGE.${PAYMENTS_DATASETS_LABELS[index]}`);
    });
  }

  private mapItemStateToDto(itemState: PaymentBarState) {
    switch (itemState) {
      case 1:
        return "PAYMENT_LINK";
      case 2:
        return "QR_CODE_PAYMENT";
      case 3:
        return "RECURRENT_PAYMENTS";
      case 4:
        return "PAYMENT_FROM_SITE";
      default:
        return;
    }
  }

  private toggleBar(itemState: PaymentBarState) {
    if (this.barStates.has(itemState)) {
      this.barStates.delete(itemState);
      this.totalPayments =
        this.totalPayments! - (this.commonData!.total_statistics.find((s: any) => s.payment_type === this.mapItemStateToDto(itemState))?.sum || 0);
      this.totalCommision =
        this.totalCommision! -
        (this.commonData!.total_statistics.find((s: any) => s.payment_type === this.mapItemStateToDto(itemState))?.commission || 0);
    } else {
      this.barStates.add(itemState);
      this.totalPayments =
        this.totalPayments! + (this.commonData!.total_statistics.find((s: any) => s.payment_type === this.mapItemStateToDto(itemState))?.sum || 0);
      this.totalCommision =
        this.totalCommision! +
        (this.commonData!.total_statistics.find((s: any) => s.payment_type === this.mapItemStateToDto(itemState))?.commission || 0);
    }

    this.data.datasets
      .filter((el: any) => el.bar === itemState)
      .forEach((item: any) => {
        item.hidden = !this.barStates.has(itemState);
        if (itemState !== PaymentBarState.Commision) {
          this.barStates.delete(PaymentBarState.AllPayments);
        }
        if (
          this.barStates.has(PaymentBarState.QrPayment) &&
          this.barStates.has(PaymentBarState.LinkPayment) &&
          this.barStates.has(PaymentBarState.RecurringPayment) &&
          this.barStates.has(PaymentBarState.SitePayment)
        ) {
          this.barStates.add(PaymentBarState.AllPayments);
        }
      });
  }

  private toggleAllBars() {
    if (this.barStates.has(PaymentBarState.AllPayments)) {
      this.totalPayments = 0;
      this.totalCommision = 0;
      Object.values(PaymentBarState)
        .filter((el: any) => !isNaN(el) && el !== PaymentBarState.Commision)
        .map((el) => {
          this.barStates.delete(el as PaymentBarState);
        });
    } else {
      this.totalPayments = this.commonData?.payments_sum_total || 0;
      this.totalCommision = this.commonData?.commission_sum_total || 0;
      Object.values(PaymentBarState)
        .filter((el: any) => !isNaN(el) && el !== PaymentBarState.Commision)
        .map((el) => {
          this.barStates.add(el as PaymentBarState);
        });
    }

    this.data.datasets
      .filter((el: any) => el.bar !== PaymentBarState.Commision)
      .forEach((item: any) => {
        item.hidden = !this.barStates.has(item.bar);
      });
  }

  private initAllBars() {
    this.barStates.add(PaymentBarState.AllPayments);
    this.barStates.add(PaymentBarState.Commision);
    this.barStates.add(PaymentBarState.QrPayment);
    this.barStates.add(PaymentBarState.LinkPayment);
    this.barStates.add(PaymentBarState.RecurringPayment);
    this.barStates.add(PaymentBarState.SitePayment);
  }

  private calculateTotalSum(date: string) {
    const index = this.data.labels.indexOf(date);
    if (index !== -1) {
      const stat = this.paymentStatistics![index];
      let totalSum = 0;
      let totalCommission = 0;

      if (this.barStates.has(this.barState.LinkPayment)) {
        totalSum += stat.paymentByLinkSum;
        totalCommission += stat.linkPaymentCommission;
      }
      if (this.barStates.has(this.barState.QrPayment)) {
        totalSum += stat.paymentByQrSum;
        totalCommission += stat.qrPaymentCommission;
      }
      if (this.barStates.has(this.barState.RecurringPayment)) {
        totalSum += stat.recurringPaymentSum;
        totalCommission += stat.recurringPaymentCommission;
      }
      if (this.barStates.has(this.barState.SitePayment)) {
        totalSum += stat.paymentByWebsiteSum;
        totalCommission += stat.sitePaymentCommission;
      }

      const result = this.barStates.has(this.barState.Commision) ? totalSum - totalCommission : totalSum;

      return Number.isInteger(result) ? result : result.toFixed(2).replace(".", ",");
    }

    return 0;
  }

  private getMinBarHeightPlugin() {
    const dateFilter = this.dateFilter;
    const barStates = this.barStates;

    return {
      id: "minBarHeightPlugin",
      beforeDatasetsDraw: function (chart: any) {
        const ctx = chart.ctx;
        const datasets = chart.data.datasets;
        const minBarHeight = 2;

        if (dateFilter === DateState.Month) {
          datasets.forEach((dataset: any, datasetIndex: any) => {
            if (!barStates.has(dataset.bar)) {
              return;
            }
            const meta = chart.getDatasetMeta(datasetIndex);
            if (!meta.hidden) {
              meta.data.forEach((bar: any, index: any) => {
                const dataValue = dataset.data[index];
                if (dataValue > 50) {
                  return;
                }
                const originalHeight = bar.height;
                const newHeight = Math.max(originalHeight, minBarHeight);
                const heightIncrease = newHeight - originalHeight;
                const y = bar.y - heightIncrease / 2;
                const x = bar.x - bar.width / 2;

                if (dataValue === 0) {
                  return;
                }

                ctx.save();
                ctx.fillStyle = dataset.backgroundColor;
                ctx.fillRect(x, y, bar.width, newHeight);
                ctx.restore();
              });
            }
          });
        }
      },
    };
  }

  renderChar() {
    this.data = this.initializeChartData();

    const minBarHeightPlugin = this.getMinBarHeightPlugin();

    const hoverSegment = {
      hoverValue: null as null | number,
      id: "hoverSegment",
      beforeDatasetsDraw(chart: any) {
        const {
          ctx,
          chartArea: { top, width, height },
          scales: { x },
        } = chart;
        ctx.save();

        const segment = width / (x.max + 1);

        ctx.fillStyle = "rgba(173, 216, 230, 0.2)";

        if (this.hoverValue || this.hoverValue === 0) {
          ctx.fillRect(x.getPixelForValue(this.hoverValue) - segment / 2, top, segment, height);
        }
      },
      afterEvent(chart: any, args: any) {
        const {
          scales: { x },
        } = chart;

        if (args.inChartArea) {
          this.hoverValue = x.getValueForPixel(args.event.x);
        } else {
          this.hoverValue = null;
        }

        args.changed = true;
      },
    };

    this.chart = new Chart("bar", {
      type: "bar",
      data: this.data,
      plugins: [hoverSegment, minBarHeightPlugin],
      options: {
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          title: {
            display: false,
          },
          legend: {
            display: false,
            position: "bottom",
          },
          tooltip: {
            enabled: false,
            external: (context) => {
              let tooltipEl = document.getElementById("chartjs-tooltip");
              const tooltipModel = context.tooltip;

              if (!tooltipEl) {
                tooltipEl = document.createElement("div");
                tooltipEl.id = "chartjs-tooltip";
                tooltipEl.innerHTML = "<table></table>";
                document.body.appendChild(tooltipEl);
              }

              if (tooltipModel.opacity === 0 || allDataZero()) {
                tooltipEl.style.opacity = "0";
                return;
              }

              function allDataZero() {
                const bodyLines = tooltipModel.body.map(getBody);
                for (let i = 0; i < bodyLines.length; i++) {
                  if (Math.ceil(bodyLines[i][0].split(":")[1]) !== 0) {
                    return false;
                  }
                }
                return true;
              }

              tooltipEl.classList.remove("above", "below", "no-transform");
              if (tooltipModel.yAlign) {
                tooltipEl.classList.add(tooltipModel.yAlign);
              } else {
                tooltipEl.classList.add("no-transform");
              }

              function getBody(bodyItem: any) {
                return bodyItem.lines;
              }

              if (tooltipModel.body) {
                const titleLines = tooltipModel.title || [];
                const bodyLines = tooltipModel.body.map(getBody);
                let innerHtml = "";
                titleLines.forEach((title) => {
                  innerHtml += `
                  <thead style="${STYLES.marginBottom(16)}">
                    <tr>
                      <th style="${STYLES.font.common} ${STYLES.font.bold} ${STYLES.title.textLeft}">
                        <div style="${STYLES.div.flexBetween}">
                          <span>${title}</span>
                          <span>${this.calculateTotalSum(title) ? `₸${this.calculateTotalSum(title)}` : ""}</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <div style="${STYLES.height(6)}"></div>
                  `;
                });

                bodyLines.forEach(function (body, i) {
                  const description = body[0].split(":")[0];
                  const value = "₸" + body[0].split(":")[1];
                  const colors = tooltipModel.labelColors[i];

                  innerHtml += `
                  <tbody>
                    <tr>
                      <td>
                        <div style="${STYLES.div.flexBetween}">
                          <div style="${STYLES.div.flex} ${STYLES.gap(4)} ${STYLES.div.alignCenter}">
                            <div style="${STYLES.width(8)} ${STYLES.height(8)} ${STYLES.borderRadius(100)} background-color: ${
                              colors.backgroundColor
                            }; border: 1px solid ${colors.borderColor};"></div>
                            <span style="${STYLES.font.common} ${STYLES.font.normal}"> ${description}</span>
                          </div>
                          <span style="${STYLES.font.common} ${STYLES.font.bold}"> ${value}</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  `;
                });

                (tooltipEl.querySelector("table") as HTMLTableElement).innerHTML = innerHtml;
              }

              const position = context.chart.canvas.getBoundingClientRect();

              const tooltipStyle = tooltipEl.style;

              Object.assign(tooltipStyle, {
                backgroundColor: "#FFF",
                width: "241px",
                padding: "8px 12px",
                position: "absolute",
                opacity: "1",
                transition: "0.3s",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                borderRadius: "6px",
                boxShadow: "0px 4px 16px 0px rgba(112, 112, 112, 0.25)",
                left: position.left / 1.75 + window.scrollX + tooltipModel.caretX + "px",
                top: position.top + window.scrollY + tooltipModel.caretY + "px",
                pointerEvents: "none",
              });
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {},
          y: {
            border: {
              dash: [3, 4],
            },
          },
        },
      },
    });
  }

  private initializeChartData() {
    const data = {
      labels: this.analyticsTranslationService.mapDatesToLabels(
        this.paymentStatistics!.map((el) => el.date),
        this.dateFilter
      ),
      datasets: [
        {
          label: "Платежная ссылка",

          data: this.paymentStatistics?.map((stat) => stat.paymentByLinkSum),
          backgroundColor: ["rgba(76, 113, 239, 1)"],
          borderRadius: 4,
          order: 1,
          hidden: false,
          bar: PaymentBarState.LinkPayment,
          stack: "group",
          categoryPercentage: 0.55,
        },
        {
          label: "Оплата по QR-код",
          data: this.paymentStatistics?.map((stat) => stat.paymentByQrSum),
          backgroundColor: ["rgba(59, 187, 147, 1)"],
          borderRadius: 4,
          order: 1,
          hidden: false,
          bar: PaymentBarState.QrPayment,
          stack: "group",
          categoryPercentage: 0.55,
        },
        {
          label: "Реккурентный платеж",
          data: this.paymentStatistics?.map((stat) => stat.recurringPaymentSum),
          backgroundColor: ["rgba(76, 171, 239, 1)"],
          borderRadius: 4,
          order: 1,
          hidden: false,
          bar: PaymentBarState.RecurringPayment,
          stack: "group",
          categoryPercentage: 0.55,
        },
        {
          label: "Оплата с сайта",

          data: this.paymentStatistics?.map((stat) => stat.paymentByWebsiteSum),
          backgroundColor: ["rgba(139, 71, 225, 1)"],
          borderRadius: 4,
          order: 1,
          hidden: false,
          bar: PaymentBarState.SitePayment,
          stack: "group",
          categoryPercentage: 0.55,
        },
        {
          label: "Комиссия",
          data: this.paymentStatistics?.map((stat) => stat.totalCommission),
          borderColor: "rgba(242, 115, 113, 1)",
          fill: true,
          backgroundColor: "rgba(242, 115, 113, .3)",
          borderWidth: {
            top: 2,
            right: 0,
            bottom: 0,
            left: 0,
          },
          order: 0,
          hidden: false,
          bar: PaymentBarState.Commision,
          grouped: false,
          categoryPercentage: 1.11,
        },
      ],
    };
    return data;
  }
}
